import {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useState,
    useSyncExternalStore,
    type ReactNode,
} from 'react';
import {
    clearAccessToken,
    getAccessToken,
    onAccessTokenChange,
    setAccessToken,
} from '@/lib/auth/tokenStore';

/** The only reason recorded today. See technical brief §2. */
export type SignOutReason = 'session-expired';

export interface SessionValue {
    status: 'signed-in' | 'signed-out';
    accessToken: string | null;
    signOutReason: SignOutReason | null;
    signIn(token: string): void;
    signOut(reason?: SignOutReason): void;
}

const SessionContext = createContext<SessionValue | null>(null);

export function useSession(): SessionValue {
    const value = useContext(SessionContext);
    if (!value) {
        throw new Error('useSession must be used inside <SessionProvider>');
    }
    return value;
}

interface SessionProviderProps {
    children: ReactNode;
}

/**
 * The authenticated session, derived from the existing `tokenStore` rather
 * than holding its own copy (state.md: "server data is not component
 * state"). `onAccessTokenChange` already returns an unsubscribe function,
 * which is exactly `useSyncExternalStore`'s subscribe contract, and
 * `getAccessToken` returns a primitive, so there is no new-object-every-
 * render loop.
 *
 * `signOutReason` is the one piece of state this provider actually owns — it
 * is not server data, and it is intentionally memory-only: a reload loses it,
 * which is what makes a reload show a clean sign-in screen with no error
 * copy (AC6).
 *
 * No persistence of any kind.
 */
export function SessionProvider({ children }: Readonly<SessionProviderProps>) {
    const token = useSyncExternalStore(onAccessTokenChange, getAccessToken);
    const [signOutReason, setSignOutReason] = useState<SignOutReason | null>(
        null
    );

    const signIn = useCallback((accessToken: string) => {
        setAccessToken(accessToken);
        setSignOutReason(null);
    }, []);

    const signOut = useCallback((reason?: SignOutReason) => {
        clearAccessToken();
        setSignOutReason(reason ?? null);
    }, []);

    const value = useMemo<SessionValue>(
        () => ({
            status: token ? 'signed-in' : 'signed-out',
            accessToken: token,
            signOutReason,
            signIn,
            signOut,
        }),
        [token, signOutReason, signIn, signOut]
    );

    return (
        <SessionContext.Provider value={value}>
            {children}
        </SessionContext.Provider>
    );
}
